import {
  Modal,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Skeleton,
  Stack,
  Divider,
  Chip,
  TextField,
  InputAdornment
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PeopleIcon from "@mui/icons-material/People";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { FETCH_CLIENT_CHAT_GROUP_PARTICIPANTS } from "../../constant/Apipath";
import { useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { Postrequestcall } from "../../request/Postrequest";
import moment from "moment";
import { BIBlue } from "../../assets/helpers";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: {
    xs: "92%",
    sm: "500px",
    md: "540px"
  },
  maxHeight: {
    xs: "88vh",
    sm: "82vh",
    md: "80vh",
    lg: "750px"
  },
  display: "flex",
  flexDirection: "column",
  bgcolor: "background.paper",
  boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
  borderRadius: "20px",
  p: { xs: 2, sm: 2.5 },
  outline: "none",
  border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "#f0f0f0"}`
};

export default function ClientShowMembersModal({ open, onClose, selectedChat, clientId, selectedClientData }) {
  const [chatGroupUsers, setChatGroupUsers] = useState([]);
  const [isMemberListLoading, setIsMemberListLoading] = useState(false);
  const [groupDetails, setGroupDetails] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const { loginData } = useSelector((state) => state?.main);

  const getChatGroupUser = async () => {
    try {
      setIsMemberListLoading(true);
      const chatGroupId = selectedChat?.chatGroupId;
      const targetClientId = clientId || selectedClientData?.ClientId || selectedChat?.clientId;
      const accessToken = loginData?.accessToken;

      if (!chatGroupId || !targetClientId || !accessToken) {
        console.error(
          "ClientShowMembersModal -> Missing parameters (chatGroupId, clientId, or accessToken)."
        );
        setIsMemberListLoading(false);
        return;
      }

      const payload = {
        ClientId: Number(targetClientId),
        ChatGroupId: Number(chatGroupId)
      };

      const res = await Postrequestcall(
        FETCH_CLIENT_CHAT_GROUP_PARTICIPANTS,
        payload,
        accessToken
      );

      if (res?.status === 200 && res?.data?.status && res?.data?.data) {
        const groupData = res.data.data[0];
        setGroupDetails(groupData);
        const members = groupData?.members || [];
        setChatGroupUsers(members);
      } else {
        console.error(
          "ClientShowMembersModal -> Error fetching client chat group participants: ",
          res
        );
        setChatGroupUsers([]);
      }
    } catch (error) {
      console.error("ClientShowMembersModal -> Error: ", error);
      setChatGroupUsers([]);
    } finally {
      setIsMemberListLoading(false);
    }
  };

  useEffect(() => {
    if (open && selectedChat?.chatGroupId) {
      setSearchQuery("");
      getChatGroupUser();
    }
  }, [open, selectedChat?.chatGroupId, clientId]);

  const groupTitle = groupDetails?.chatGroupName || selectedChat?.groupName || "Group Members";

  const createdByUser = groupDetails?.createdByUser || groupDetails?.createdBy || selectedChat?.createdByUser;
  const createdDate = groupDetails?.dateCreated || selectedChat?.dateCreated;
  const formattedDate = createdDate ? moment(createdDate).format("MMM DD, YYYY [at] h:mm A") : null;

  // Filtered members based on search query
  const filteredMembers = useMemo(() => {
    if (!searchQuery.trim()) return chatGroupUsers;
    const query = searchQuery.toLowerCase().trim();
    return chatGroupUsers.filter((member) => {
      const fullName = `${member.firstName || ""} ${member.lastName || ""}`.toLowerCase();
      const trade = (member.tradeName || "").toLowerCase();
      const employeeId = String(member.employeeId || "").toLowerCase();
      return fullName.includes(query) || trade.includes(query) || employeeId.includes(query);
    });
  }, [chatGroupUsers, searchQuery]);

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="client-members-modal">
      <Box sx={style}>
        {/* Header */}
        <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
            <Avatar
              sx={{
                bgcolor: `${BIBlue}15`,
                color: BIBlue,
                width: 38,
                height: 38
              }}>
              <PeopleIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: "16px", color: "text.primary" }}>
                Group Members
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "12px" }}>
                {groupTitle}{" "}
                {!isMemberListLoading &&
                  (groupDetails?.memberCount != null
                    ? `(${groupDetails.memberCount})`
                    : chatGroupUsers.length > 0
                    ? `(${chatGroupUsers.length})`
                    : "")}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small" sx={{ color: "text.secondary" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        {/* Group Creation Info Section */}
        {(createdByUser || formattedDate) && (
          <Box
            sx={{
              p: 1.2,
              px: 1.5,
              mb: 1.5,
              bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "#f8fafc"),
              borderRadius: "12px",
              border: "1px solid",
              borderColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "#e2e8f0")
            }}>
            <Stack spacing={0.5}>
              {createdByUser && (
                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                  <PersonIcon sx={{ fontSize: 16, color: BIBlue }} />
                  <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "12px" }}>
                    Created by:{" "}
                    <Typography component="span" variant="caption" sx={{ fontWeight: 600, color: "text.primary", fontSize: "12px" }}>
                      {createdByUser}
                    </Typography>
                  </Typography>
                </Stack>
              )}
              {formattedDate && (
                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                  <CalendarTodayIcon sx={{ fontSize: 14, color: BIBlue }} />
                  <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "12px" }}>
                    Created on:{" "}
                    <Typography component="span" variant="caption" sx={{ fontWeight: 600, color: "text.primary", fontSize: "12px" }}>
                      {formattedDate}
                    </Typography>
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Box>
        )}

        {/* Search Bar */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search members by name or role..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            mb: 1.5,
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#ffffff"),
              fontSize: "13.5px"
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery("")}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />

        <Divider sx={{ mb: 1.5 }} />

        {/* Member List */}
        <List
          sx={{
            p: 0,
            flexGrow: 1,
            maxHeight: { xs: "48vh", sm: "45vh", md: "42vh", lg: "420px" },
            overflowY: "auto"
          }}>
          {isMemberListLoading ? (
            Array.from(new Array(3)).map((_, index) => (
              <ListItem
                key={index}
                sx={{
                  borderRadius: "12px",
                  border: "1px solid #f0f0f0",
                  mb: 1,
                  p: 1.5
                }}>
                <ListItemAvatar>
                  <Skeleton variant="circular" width={40} height={40} />
                </ListItemAvatar>
                <ListItemText
                  primary={<Skeleton width="60%" height={20} />}
                  secondary={<Skeleton width="40%" height={16} />}
                />
              </ListItem>
            ))
          ) : filteredMembers.length === 0 ? (
            <Typography variant="body2" sx={{ textAlign: "center", py: 3, color: "text.secondary" }}>
              {searchQuery ? `No members found matching "${searchQuery}"` : "No members found"}
            </Typography>
          ) : (
            filteredMembers.map((member) => {
              const currentUserId = Number(loginData?.userInformation?.UserId);
              const memberUserId = Number(member.employeeUserId || member.userId);
              const isMe = member.contactStatus === "me" || (memberUserId > 0 && memberUserId === currentUserId);
              const avatarSrc = member.userProfilePicture || member.pictureUrl;
              const uniqueKey = member.chatGroupUserId || member.employeeId || member.employeeUserId || member.id;

              return (
                <ListItem
                  key={uniqueKey}
                  sx={{
                    border: "1px solid",
                    borderColor: (theme) =>
                      theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "#f1f5f9",
                    borderRadius: "14px",
                    mb: 1,
                    p: 1.2,
                    px: 1.5,
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "#fafbfc"
                  }}
                  secondaryAction={
                    isMe ? (
                      <Chip
                        label="You"
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: "11px",
                          bgcolor: `${BIBlue}15`,
                          color: BIBlue,
                          borderRadius: "8px"
                        }}
                      />
                    ) : null
                  }>
                  <ListItemAvatar>
                    <Avatar
                      src={avatarSrc}
                      alt={`${member.firstName || ""} ${member.lastName || ""}`}
                      sx={{ width: 40, height: 40, border: `1px solid ${BIBlue}20` }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary", fontSize: "14px" }}>
                        {`${member.firstName || ""} ${member.lastName || ""}`.trim() || "Member"}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "12px" }}>
                        {member.tradeName || (member.employeeId ? `Employee ID: ${member.employeeId}` : "Member")}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            })
          )}
        </List>
      </Box>
    </Modal>
  );
}
